import type { ComponentProps } from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { useFormFieldContext } from "../FormField/context";
import { Trigger, ChevronWrap, Content, Viewport, Item, LeadSlot } from "./style.radix";
import ChevronDownIcon from "../../../assets/icons/lucide/ChevronDownIcon";

/** The value that stands for "none" inside Radix, which refuses an empty item value. */
const EMPTY = "__vazio__";
import type { SelectProps, SelectOptionProps } from "./types.radix";
import LegacySelect from "./LegacySelect";

function SelectRoot({
  value,
  defaultValue,
  onValueChange,
  placeholder = "Selecione...",
  disabled,
  size = "md",
  children,
  name,
  required,
  emptyLabel,
  ariaLabel,
  id,
  invalid,
  leading,
}: SelectProps) {
  const ctx = useFormFieldContext();
  const rootProps: ComponentProps<typeof RadixSelect.Root> = {};
  const toRadix = (v: string) => (emptyLabel !== undefined && v === "" ? EMPTY : v);
  if (value !== undefined) rootProps.value = toRadix(value);
  if (defaultValue !== undefined) rootProps.defaultValue = toRadix(defaultValue);
  else if (value === undefined && emptyLabel !== undefined) rootProps.defaultValue = EMPTY;
  if (onValueChange !== undefined)
    rootProps.onValueChange = (v) => {
      // Radix refuses "" as an item value, so no person ever picks it; the empty choice is EMPTY. A bare "" comes only
      // from the native select Radix keeps inside a form, which falls back to "" while the value is not among the
      // options (a list still loading, or a value the viewer's list lacks). Passing it on wiped a filled field and
      // blocked the save with "obrigatório" (EditProfile's hospital, measured 2026-10-29).
      if (v === "") return;
      onValueChange(v === EMPTY ? "" : v);
    };
  if (disabled !== undefined) rootProps.disabled = disabled;
  if (name !== undefined) rootProps.name = name;
  const req = required ?? ctx?.required;
  if (req !== undefined) rootProps.required = req;
  return (
    <RadixSelect.Root {...rootProps}>
      <Trigger
        $size={size}
        $hasLeading={leading !== undefined}
        aria-describedby={ctx?.ariaDescribedBy}
        aria-invalid={invalid ?? ctx?.ariaInvalid}
        aria-label={ariaLabel}
        id={id ?? ctx?.id}
      >
        {leading !== undefined && <LeadSlot aria-hidden="true">{leading}</LeadSlot>}
        <RadixSelect.Value placeholder={placeholder} />
        <ChevronWrap>
          <ChevronDownIcon width={18} height={18} aria-hidden="true" />
        </ChevronWrap>
      </Trigger>
      <RadixSelect.Portal>
        <Content position="popper" sideOffset={6}>
          <Viewport>
            {emptyLabel !== undefined && <Item value={EMPTY}><RadixSelect.ItemText>{emptyLabel}</RadixSelect.ItemText></Item>}
            {children}
          </Viewport>
        </Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

function SelectOption({ value, disabled, children }: SelectOptionProps) {
  const itemProps: { value: string; disabled?: boolean } = { value };
  if (disabled !== undefined) itemProps.disabled = disabled;
  return (
    <Item {...itemProps}>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </Item>
  );
}

export const Select = Object.assign(SelectRoot, { Option: SelectOption });
export type { SelectProps, SelectOptionProps, SelectSize, SelectChangeEvent } from "./types.radix";

// Legacy default export (react-select wrapper) — mantido para consumidores existentes
export default LegacySelect;
